import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Icon, ICONS } from '@/lib/icons'
import { useState } from 'react'

// Single source of truth for app version
const APP_VERSION = '2.4.0'

export function UserManualEN() {
  const [changelogOpen, setChangelogOpen] = useState(false)
  return (
    <div className="container mx-auto px-4 py-8 max-w-5xl">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-foreground mb-2">User Manual</h1>
        <p className="text-lg text-muted-foreground">Complete guide for Klubbspel tournament management</p>
      </div>

      {/* Introduction */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Icon icon={ICONS.info} className="text-info" />
            Welcome to Klubbspel
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-lg">
            Klubbspel is a free tournament management system for everyone who wants to organize series and tournaments in racket and board sports.
          </p>
          <p>
            Perfect for sports clubs, friend groups, companies with break-time activities, families, or anyone who wants to track matches and rankings.
            The system handles tournament series, match reporting, ELO-based ranking, and automatic score calculations – easy to use for everyone!
          </p>
          <div className="bg-info-muted border border-info/30 rounded-lg p-4 mt-4 text-info-muted-foreground">
            <p className="font-semibold mb-2">🚀 Quick Start</p>
            <ol className="list-decimal list-inside space-y-1">
              <li><a href="/login" className="underline hover:text-info">Log in</a> with your email (passwordless)</li>
              <li>Create or join a club</li>
              <li>Start a tournament series</li>
              <li>Invite players and report matches</li>
            </ol>
          </div>
          <p className="font-semibold text-foreground">
            Supported sports (examples - more will be added):
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="flex items-center gap-2 p-2 bg-muted rounded">
              <Icon icon={ICONS.tableTennis} className="text-orange-600" />
              <span>Table Tennis</span>
            </div>
            <div className="flex items-center gap-2 p-2 bg-muted rounded">
              <Icon icon={ICONS.tennis} className="text-green-600" />
              <span>Tennis</span>
            </div>
            <div className="flex items-center gap-2 p-2 bg-muted rounded">
              <Icon icon={ICONS.badminton} className="text-info" />
              <span>Badminton</span>
            </div>
            <div className="flex items-center gap-2 p-2 bg-muted rounded">
              <Icon icon={ICONS.padel} className="text-purple-600" />
              <span>Padel</span>
            </div>
            <div className="flex items-center gap-2 p-2 bg-muted rounded">
              <Icon icon={ICONS.squash} className="text-yellow-600" />
              <span>Squash</span>
            </div>
            <div className="flex items-center gap-2 p-2 bg-muted rounded">
              <Icon icon={ICONS.pickleball} className="text-pink-600" />
              <span>Pickleball</span>
            </div>
            <div className="flex items-center gap-2 p-2 bg-muted rounded">
              <Icon icon={ICONS.chess} className="text-muted-foreground" />
              <span>Chess</span>
            </div>
            <div className="flex items-center gap-2 p-2 bg-muted rounded">
              <Icon icon={ICONS.beachTennis} className="text-cyan-600" />
              <span>Beach Tennis</span>
            </div>
            <div className="flex items-center gap-2 p-2 bg-muted rounded">
              <Icon icon={ICONS.tableHockey} className="text-red-600" />
              <span>Table Hockey</span>
            </div>
            <div className="flex items-center gap-2 p-2 bg-muted rounded">
              <Icon icon={ICONS.foosball} className="text-amber-700" />
              <span>Foosball</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Getting Started */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Icon icon={ICONS.user} className="text-info" />
            Getting Started
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h3 className="font-semibold text-lg mb-2">1. Log In</h3>
            <p className="text-muted-foreground mb-3">
              Click "Log in" in the top right corner. Enter your email address and we'll send you a magic link to confirm your identity.
              No complicated password management - just click the link in the email.
            </p>
          </div>
          
          <div>
            <h3 className="font-semibold text-lg mb-2">2. Join or Create a Club</h3>
            <p className="text-muted-foreground mb-3">
              Go to the "Clubs" tab and either join an existing club or create a new one. To create matches and tournaments,
              you must belong to a club.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-lg mb-2">3. Complete Your Profile</h3>
            <p className="text-muted-foreground mb-3">
              Before you can participate in competitions, you must add your first and last name in settings. This ensures that
              other players can identify you in the results.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Permissions */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Icon icon={ICONS.shield} className="text-info" />
            Permissions and Roles
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-muted-foreground mb-3">
            Klubbspel uses a three-tier permission system:
          </p>

          <div className="space-y-4">
            <div className="border-l-4 border-purple-500 pl-4">
              <h3 className="font-semibold text-lg mb-1">Platform Owner</h3>
              <p className="text-muted-foreground">
                Has full control over all clubs, series, and matches. Can perform administrative actions anywhere in the system.
              </p>
            </div>

            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="font-semibold text-lg mb-1">Club Administrator</h3>
              <p className="text-muted-foreground mb-2">
                Manages their club's tournaments and members. Can:
              </p>
              <ul className="list-disc ml-6 space-y-1 text-muted-foreground">
                <li>Create and manage tournament series</li>
                <li>Edit or delete any matches in the club's series</li>
                <li>Generate knockout brackets</li>
                <li>Complete tournaments and lock results</li>
                <li>Add players to the club</li>
              </ul>
            </div>

            <div className="border-l-4 border-green-500 pl-4">
              <h3 className="font-semibold text-lg mb-1">Player</h3>
              <p className="text-muted-foreground mb-2">
                Can manage their own matches. Can:
              </p>
              <ul className="list-disc ml-6 space-y-1 text-muted-foreground">
                <li>Report results for matches they participate in</li>
                <li>Edit their own matches (before they are locked)</li>
                <li>View all tournaments and leaderboards</li>
                <li>Export match data to CSV</li>
              </ul>
            </div>
          </div>

          <div className="bg-warning-muted border border-warning rounded p-4 mt-4">
            <p className="text-sm text-muted-foreground">
              <strong>Note:</strong> When a match has been played and a subsequent match in the same tournament tree is reported,
              the first match is automatically locked. This prevents results from being changed in a way that would affect already
              played matches. Only administrators can unlock locked matches.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Tournament Formats */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Icon icon={ICONS.trophy} className="text-info" />
            Tournament Formats
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <h3 className="font-semibold text-lg mb-2 flex items-center gap-2">
              <Icon icon={ICONS.tournament} className="text-orange-600" />
              Open Series (Round-robin)
            </h3>
            <p className="text-muted-foreground mb-2">
              Everyone plays everyone. Perfect for season leagues where all participants get a chance to face each other.
              Ranked using ELO points or win percentage.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-lg mb-2 flex items-center gap-2">
              <Icon icon={ICONS.chart} className="text-green-600" />
              Ladder Tournaments
            </h3>
            <p className="text-muted-foreground mb-3">
              Players are ranked in ladder positions. You can challenge any player, but it's most advantageous to challenge upward.
            </p>
            
            <div className="bg-muted p-4 rounded space-y-4">
              <div>
                <p className="font-semibold mb-2">When you challenge upward and win:</p>
                <p className="text-sm text-muted-foreground mb-2">
                  You jump to the challenged player's position and all players in between are pushed down one step.
                </p>
                <div className="bg-info-muted p-2 rounded text-sm text-info-muted-foreground">
                  <p className="font-medium">Example: You are #5 and beat #2</p>
                  <p>→ You become #2, former #2 becomes #3, former #3 becomes #4, former #4 becomes #5</p>
                </div>
              </div>

              <div>
                <p className="font-semibold mb-2">When you challenge upward and lose:</p>
                <ul className="list-disc ml-6 space-y-1 text-sm text-muted-foreground">
                  <li><strong>Classic variant:</strong> No change - you keep your position (no penalty)</li>
                  <li><strong>Aggressive variant:</strong> You drop one step - swap places with the player below you (penalty for failed challenge)</li>
                </ul>
              </div>

              <div>
                <p className="font-semibold mb-2">When you challenge downward:</p>
                <p className="text-sm text-muted-foreground mb-1">
                  If a higher ranked player challenges someone lower ranked:
                </p>
                <ul className="list-disc ml-6 space-y-1 text-sm text-muted-foreground">
                  <li><strong>If higher ranked wins:</strong> No change in positions (expected result)</li>
                  <li><strong>If higher ranked loses:</strong> Same rules as "challenge upward and win" apply - winner jumps up</li>
                </ul>
              </div>

              <div className="bg-warning-muted border border-warning rounded p-3 mt-3">
                <p className="text-sm font-semibold text-foreground mb-1">💡 Strategy:</p>
                <p className="text-sm text-muted-foreground">
                  It's most advantageous to challenge players higher up the ladder. Challenging downward risks losing your position with no opportunity for gain.
                </p>
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-lg mb-2 flex items-center gap-2">
              <Icon icon={ICONS.trophy} className="text-purple-600" />
              Cup Format (Knockout)
            </h3>
            <p className="text-muted-foreground mb-3">
              Single elimination - lose and you're out. Supports 4, 8, 16, or 32 players. Winners advance to the next round
              until only one champion remains.
            </p>
            <div className="bg-info-muted border border-info/30 rounded p-3 text-info-muted-foreground">
              <p className="text-sm">
                <strong>Bronze Match:</strong> Optionally, you can enable a third-place match between the semi-final losers.
              </p>
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-lg mb-2 flex items-center gap-2">
              <Icon icon={ICONS.users} className="text-pink-600" />
              Group Stage + Knockout
            </h3>
            <p className="text-muted-foreground mb-3">
              Combines group play (round-robin) with a subsequent knockout bracket. Players first compete in all-against-all
              in smaller groups, then the best players advance to the knockout stage.
            </p>
            
            <div className="space-y-3">
              <div>
                <p className="font-semibold mb-1">Group Stage Rules:</p>
                <ul className="list-disc ml-6 space-y-1 text-sm text-muted-foreground">
                  <li>Supports 2-8 groups with 3-8 players per group</li>
                  <li>
                    Point system (sport-specific):
                    <ul className="list-circle ml-6 mt-1 space-y-1">
                      <li><strong>Racket/Paddle Sports</strong> (Table Tennis, Tennis, Badminton, Squash, Padel, Pickleball): Win = 2pts, Draw = 1pt, Loss = 0pts</li>
                      <li><strong>Chess</strong>: Win = 1.0pt, Draw = 0.5pt, Loss = 0.0pt (traditional FIDE system)</li>
                      <li><strong>Goal-Based Sports</strong> (future: Football, Hockey, Basketball): Win = 3pts, Draw = 1pt, Loss = 0pts</li>
                    </ul>
                  </li>
                  <li>For chess: Draws are allowed in group stage</li>
                  <li>Automatic calculation of group standings</li>
                  <li>Sorting: Points → Head-to-head → Goal difference → Number of wins</li>
                </ul>
              </div>

              <div>
                <p className="font-semibold mb-1">Handling Odd Numbers of Players:</p>
                <div className="bg-warning-muted border border-warning rounded p-3 text-sm">
                  <p className="text-muted-foreground mb-2">
                    <strong>Example with 3 players in a group:</strong>
                  </p>
                  <p className="text-muted-foreground">
                    If the group has 3 players instead of 4, each player plays fewer matches (2 matches instead of 3).
                    This doesn't affect the point system - the player with the most points advances. In case of equal points,
                    set difference is used as a tiebreaker.
                  </p>
                </div>
              </div>

              <div>
                <p className="font-semibold mb-1">Advancement to Knockout:</p>
                <p className="text-sm text-muted-foreground mb-2">
                  When the group stage is complete, the administrator can generate the knockout bracket. The system automatically
                  takes the top N players from each group (usually top 2) and creates a bracket based on their group placements.
                </p>
                <div className="bg-info-muted border border-info/30 rounded p-3 text-sm text-info-muted-foreground">
                  <p>
                    <strong>Important:</strong> When the knockout stage has been generated, all group stage matches are automatically
                    locked to prevent changes that would affect the playoff participants.
                  </p>
                </div>
              </div>

              <div>
                <p className="font-semibold mb-1">Knockout Rules:</p>
                <ul className="list-disc ml-6 space-y-1 text-sm text-muted-foreground">
                  <li>Single elimination - one loss and you're out</li>
                  <li>For chess: Draws are NOT allowed in knockout (winner must be decided)</li>
                  <li>Matches are locked when subsequent matches are played</li>
                  <li>Bracket size is determined by the number of players advancing from groups</li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Reporting Matches */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Icon icon={ICONS.edit} className="text-info" />
            Reporting Matches
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h3 className="font-semibold text-lg mb-2">Who Can Report a Match?</h3>
            <ul className="list-disc ml-6 space-y-1 text-muted-foreground">
              <li><strong>Participating players:</strong> Can report their own matches</li>
              <li><strong>Club administrators:</strong> Can report any matches in their club</li>
              <li><strong>Platform owners:</strong> Can report any match</li>
            </ul>
          </div>

          <div>
            <h3 className="font-semibold text-lg mb-2">Set-Based Reporting (Racket Sports)</h3>
            <p className="text-muted-foreground mb-3">
              For table tennis, tennis, badminton, padel, squash, etc., you report the number of sets won by each player.
              The system does NOT record individual points within sets - only the number of sets won.
            </p>
            <div className="bg-muted p-3 rounded text-sm space-y-2">
              <p><strong>Example (Best-of-5):</strong></p>
              <p>Player A: 3 sets</p>
              <p>Player B: 1 set</p>
              <p className="text-muted-foreground italic">Individual set scores (11-9, 8-11, 11-7, 11-5) are not recorded by the system.</p>
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-lg mb-2">Outcome-Based Reporting (Chess)</h3>
            <p className="text-muted-foreground mb-2">
              For chess, you select the result using radio buttons:
            </p>
            <ul className="list-disc ml-6 space-y-1 text-muted-foreground">
              <li>Player A wins (1-0)</li>
              <li>Player B wins (0-1)</li>
              <li>Draw (½-½) - only allowed in group stage</li>
            </ul>
          </div>

          <div className="bg-warning-muted border border-warning rounded p-4">
            <p className="text-sm text-muted-foreground">
              <strong>Note:</strong> All matches must be played within the series start and end dates. The system validates that the
              match date falls within this range.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Podium and Celebration */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Icon icon={ICONS.podium} className="text-info" />
            Podium and Celebration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-muted-foreground">
            When a tournament is completed, the system displays a beautiful Olympic-style three-tier podium with colorful
            confetti to celebrate the winners!
          </p>
          
          <div className="bg-gradient-to-br from-warning-muted to-warning-muted border-2 border-warning rounded-lg p-6">
            <div className="text-center mb-4">
              <h3 className="text-xl font-bold text-foreground mb-2">🏆 Tournament Podium 🎉</h3>
              <p className="text-sm text-muted-foreground italic">
                (Image shows podium with confetti animation when tournament completes)
              </p>
            </div>
            
            <div className="flex items-end justify-center gap-4">
              {/* Silver (2nd place) */}
              <div className="flex flex-col items-center">
                <div className="text-4xl mb-2">🥈</div>
                <div className="bg-gray-300 rounded-t-lg px-6 py-8 text-center">
                  <p className="font-bold text-foreground">2nd Place</p>
                  <p className="text-sm text-muted-foreground">Silver</p>
                </div>
              </div>
              
              {/* Gold (1st place - taller) */}
              <div className="flex flex-col items-center">
                <div className="text-5xl mb-2">🏆</div>
                <div className="bg-yellow-400 rounded-t-lg px-6 py-12 text-center border-2 border-yellow-600">
                  <p className="font-bold text-foreground text-lg">Champion! 🎉</p>
                  <p className="text-sm text-foreground">Gold</p>
                </div>
              </div>
              
              {/* Bronze (3rd place) */}
              <div className="flex flex-col items-center">
                <div className="text-4xl mb-2">🥉</div>
                <div className="bg-orange-300 rounded-t-lg px-6 py-6 text-center">
                  <p className="font-bold text-foreground">3rd Place</p>
                  <p className="text-sm text-muted-foreground">Bronze</p>
                </div>
              </div>
            </div>
            
            <div className="mt-6 text-center">
              <p className="text-xs text-muted-foreground">
                ✨ Confetti rains down over the winners! ✨
              </p>
            </div>
          </div>

          <div className="space-y-3 mt-4">
            <p className="font-semibold">Podium Features:</p>
            <ul className="list-disc ml-6 space-y-1 text-muted-foreground">
              <li>Olympic-style three-tier display (gold highest, silver and bronze on sides)</li>
              <li>Confetti animation when podium is first shown</li>
              <li>Shows player names, matches played, wins, and win percentage</li>
              <li>Share functionality to spread results on social media</li>
              <li>Export to CSV for documentation</li>
              <li>Available for all tournament formats when completed</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Advanced Features */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Icon icon={ICONS.userCircle} className="text-info" />
            Advanced Features
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h3 className="font-semibold text-lg mb-2">CSV Export</h3>
            <p className="text-muted-foreground">
              Export match data and leaderboards to CSV format for analysis in Excel or other tools.
              Useful for season statistics and annual reporting.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-lg mb-2">Merge Players</h3>
            <p className="text-muted-foreground">
              If a player has been registered multiple times (e.g., with and without email), administrators can merge
              player profiles. This preserves all match history and moves all data to a single profile.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-lg mb-2">ELO Rating System</h3>
            <p className="text-muted-foreground mb-2">
              Klubbspel uses standard ELO calculation for dynamic rankings:
            </p>
            <ul className="list-disc ml-6 space-y-1 text-muted-foreground">
              <li>K-factor: 32 (standard for club play)</li>
              <li>Starting rating: 1500 for new players</li>
              <li>Draws give 0.5 points to both players (chess)</li>
              <li>Ratings update in real-time after each match</li>
            </ul>
          </div>

          <div>
            <h3 className="font-semibold text-lg mb-2">Search and Filters</h3>
            <p className="text-muted-foreground">
              Quick search is available for players and tournament series. Filter players by club,
              search by name or email, and find tournaments by sport or status.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Tips and Best Practices */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Icon icon={ICONS.alertCircle} className="text-info" />
            Tips and Best Practices
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="border-l-4 border-green-500 pl-4">
            <p className="font-semibold mb-1">Report matches immediately</p>
            <p className="text-sm text-muted-foreground">
              Report match results as soon as possible after the match to keep leaderboards updated and
              give all players current information.
            </p>
          </div>

          <div className="border-l-4 border-blue-500 pl-4">
            <p className="font-semibold mb-1">Check dates</p>
            <p className="text-sm text-muted-foreground">
              The match must be played within the series start and end dates. Check the dates before reporting.
            </p>
          </div>

          <div className="border-l-4 border-purple-500 pl-4">
            <p className="font-semibold mb-1">Use group stage + knockout for large tournaments</p>
            <p className="text-sm text-muted-foreground">
              For tournaments with many players (16+), group stage gives everyone the chance to play multiple matches before
              the best advance to the knockout stage.
            </p>
          </div>

          <div className="border-l-4 border-orange-500 pl-4">
            <p className="font-semibold mb-1">Enable bronze match for cup format</p>
            <p className="text-sm text-muted-foreground">
              Having a third-place match lets the semi-final losers play one more match and creates a
              complete podium with three positions.
            </p>
          </div>

          <div className="border-l-4 border-pink-500 pl-4">
            <p className="font-semibold mb-1">Communicate rules clearly</p>
            <p className="text-sm text-muted-foreground">
              Make sure all participants understand the tournament format, whether draws are allowed (chess group stage),
              and how advancement to knockout works.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Version History / Changelog */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Icon icon={ICONS.history} className="text-purple-600" />
            Version History
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Collapsible open={changelogOpen} onOpenChange={setChangelogOpen}>
            <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-muted hover:bg-accent rounded transition-colors">
              <span className="font-medium text-foreground">View changelog (v1.0.0 → v2.4.0)</span>
              <Icon 
                icon={changelogOpen ? ICONS.chevronUp : ICONS.chevronDown} 
                className="text-muted-foreground"
                width={20}
                height={20}
              />
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-4">
              <div className="space-y-6 max-h-[600px] overflow-y-auto pr-2">
                
                {/* v2.4.0 */}
                <div className="border-l-4 border-purple-500 pl-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-bold text-lg">v2.4.0</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">December 8, 2025</p>
                  <h4 className="font-semibold text-purple-700 mb-1">Visual Themes & Background Images</h4>
                  <ul className="text-sm space-y-1 text-muted-foreground list-disc list-inside">
                    <li>New: ThemePicker with 7 background options (6 sports + geometric)</li>
                    <li>Background images for Table Tennis, Tennis, Badminton, Chess, Table Hockey, Padel</li>
                    <li>Optimized images (60-80% smaller) for faster loading</li>
                    <li>Semantic color system for better consistency</li>
                    <li>Theme preferences persist across sessions</li>
                    <li>Improved visual customization</li>
                  </ul>
                </div>

                {/* v2.3.0 */}
                <div className="border-l-4 border-purple-500 pl-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-bold text-lg">v2.3.0</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">December 5, 2025</p>
                  <h4 className="font-semibold text-purple-700 mb-1">Table Hockey, Foosball & Zero Security Issues</h4>
                  <ul className="text-sm space-y-1 text-muted-foreground list-disc list-inside">
                    <li>New: Support for table hockey 🏒 (bordshockey) as a sport</li>
                    <li>New: Support for foosball 🤾 as a sport</li>
                    <li>SBHF-compliant validation rules: 0-50 goals per match</li>
                    <li>Draw rules: Allowed in group stage, rejected in playoffs</li>
                    <li>Sport-aware API: Dynamic validation based on series sport type</li>
                    <li>Security: Achieved 0 gosec issues (down from 53)</li>
                    <li>Fixed: Routing issue where unauthenticated users were incorrectly redirected</li>
                    <li>Cleaned: Removed all V2 API artifacts</li>
                    <li>Tested: 10 API integration tests + 15 unit tests</li>
                  </ul>
                </div>

                {/* v2.2.0 */}
                <div className="border-l-4 border-purple-500 pl-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-bold text-lg">v2.2.0</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">December 5, 2025</p>
                  <h4 className="font-semibold text-purple-700 mb-1">SEO Improvements</h4>
                  <ul className="text-sm space-y-1 text-muted-foreground list-disc list-inside">
                    <li>Rich noscript content for search engine indexing (~400 words)</li>
                    <li>Redirect unauthenticated visitors to manual page</li>
                    <li>Enhanced user manual with quick start guide</li>
                  </ul>
                </div>

                {/* v2.1.5 */}
                <div className="border-l-4 border-purple-500 pl-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-bold text-lg">v2.1.5</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">November 8, 2025</p>
                  <h4 className="font-semibold text-purple-700 mb-1">Pagination Fixes & SEO Improvements</h4>
                  <ul className="text-sm space-y-1 text-muted-foreground list-disc list-inside">
                    <li>Fixed: Duplicate results in pagination for players, clubs, and series</li>
                    <li>Implemented: Generic keyset pagination system with compound cursors</li>
                    <li>Added: Meta description for better search engine visibility</li>
                    <li>Added: Complete favicon system (SVG + PNG in all sizes)</li>
                    <li>Added: XML sitemap for improved search engine indexing</li>
                    <li>Fixed: robots.txt format (was serving HTML)</li>
                    <li>Added: Preconnect hints for ~300ms faster page loads</li>
                    <li>Improved: JavaScript bundle splitting for better caching</li>
                    <li>Fixed: Navigation color contrast now meets accessibility standards</li>
                    <li>Added: Content Security Policy headers for improved security</li>
                  </ul>
                </div>

                {/* v2.1.4 */}
                <div className="border-l-4 border-purple-500 pl-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-bold text-lg">v2.1.4</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">October 29, 2025</p>
                  <h4 className="font-semibold text-purple-700 mb-1">Mobile Match Display Improvements</h4>
                  <ul className="text-sm space-y-1 text-muted-foreground list-disc list-inside">
                    <li>Fixed: Mobile match cards now preserve player order (A vs B)</li>
                    <li>Winner displayed in bold regardless of left/right position</li>
                    <li>Prevents confusion about home/away or challenger/defender roles</li>
                    <li>Maintains consistency with desktop table view</li>
                  </ul>
                </div>

                {/* v2.1.3 */}
                <div className="border-l-4 border-purple-500 pl-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-bold text-lg">v2.1.3</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">October 26, 2025</p>
                  <h4 className="font-semibold text-purple-700 mb-1">Player Self-Delete and GDPR Compliance</h4>
                  <ul className="text-sm space-y-1 text-muted-foreground list-disc list-inside">
                    <li>New: Self-delete account feature in Settings page</li>
                    <li>Smart deletion: Full delete if no matches, anonymization if historical data exists</li>
                    <li>GDPR compliant: All personal information removed upon deletion</li>
                    <li>Match history preserved with anonymized player names</li>
                    <li>Automatic API token revocation on account deletion</li>
                    <li>Confirmation dialog with clear warnings</li>
                  </ul>
                </div>

                {/* v2.1.2 */}
                <div className="border-l-4 border-purple-500 pl-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-bold text-lg">v2.1.2</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">October 25, 2025</p>
                  <h4 className="font-semibold text-purple-700 mb-1">Frontend Modernization and Developer Experience</h4>
                  <ul className="text-sm space-y-1 text-muted-foreground list-disc list-inside">
                    <li>Upgraded to Vite 7 with improved build performance</li>
                    <li>Major dependency updates: recharts v3, date-fns 4, zod 4, uuid 13</li>
                    <li>Fixed: Unsafe type assertion in chart tooltip</li>
                    <li>Fixed: PlayerSelector infinite loop (2000+ API requests)</li>
                    <li>Enhanced developer documentation (setup-codex.sh)</li>
                  </ul>
                </div>

                {/* v2.1.1 */}
                <div className="border-l-4 border-purple-500 pl-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-bold text-lg">v2.1.1</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">October 24, 2025</p>
                  <h4 className="font-semibold text-purple-700 mb-1">Enhanced Player Search and Bug Fixes</h4>
                  <ul className="text-sm space-y-1 text-muted-foreground list-disc list-inside">
                    <li>Fixed: Player "Joined" date displayed as "Unknown"</li>
                    <li>Fixed: MongoDB error during paginated search results</li>
                    <li>New: Relevance scoring for player search (exact matches prioritized)</li>
                    <li>Improved UX: Better player selector with club names</li>
                  </ul>
                </div>

                {/* v2.1.0 */}
                <div className="border-l-4 border-purple-500 pl-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-bold text-lg">v2.1.0</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">October 17, 2025</p>
                  <h4 className="font-semibold text-purple-700 mb-1">Sport-Specific Points Systems</h4>
                  <ul className="text-sm space-y-1 text-muted-foreground list-disc list-inside">
                    <li>Racket/Paddle sports: 2-1-0 system (ITTF/BWF)</li>
                    <li>Chess: 1.0-0.5-0.0 display (FIDE standard)</li>
                    <li>Goal-based sports: 3-1-0 ready (FIFA/IIHF/FIBA)</li>
                    <li>Info boxes showing current points system in group stage</li>
                  </ul>
                </div>

                {/* v2.0.1 */}
                <div className="border-l-4 border-red-500 pl-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-bold text-lg">v2.0.1</span>
                    <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">Security Fix</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">October 11, 2025</p>
                  <h4 className="font-semibold text-red-700 mb-1">Match Authorization</h4>
                  <ul className="text-sm space-y-1 text-muted-foreground list-disc list-inside">
                    <li>Fixed vulnerability where unauthenticated users saw edit/delete buttons</li>
                    <li>3-tier permission system: platform owner → club admin → player</li>
                    <li>Buttons now completely hidden when user lacks permissions</li>
                  </ul>
                </div>

                {/* v2.0.0 */}
                <div className="border-l-4 border-blue-500 pl-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-bold text-lg">v2.0.0</span>
                    <span className="text-xs bg-blue-100 text-info-foreground px-2 py-1 rounded">Major Release</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">October 11, 2025</p>
                  <h4 className="font-semibold text-blue-700 mb-1">Knockout Tournament with Group Stage</h4>
                  <ul className="text-sm space-y-1 text-muted-foreground list-disc list-inside">
                    <li>Complete group stage + knockout bracket system</li>
                    <li>Olympic-style podium visualization with bronze match</li>
                    <li>Seeding strategies and byes handling</li>
                    <li>Mobile-optimized match dialogs</li>
                  </ul>
                </div>

                {/* v1.5.0 */}
                <div className="border-l-4 border-green-500 pl-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-bold text-lg">v1.5.0</span>
                    <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">New Feature</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">October 5, 2025</p>
                  <h4 className="font-semibold text-green-700 mb-1">Chess Support</h4>
                  <ul className="text-sm space-y-1 text-muted-foreground list-disc list-inside">
                    <li>Outcome-based scoring (1-0, 0-1, 1-1)</li>
                    <li>Drawn matches tracking in leaderboard</li>
                    <li>Match editing enabled for all sports</li>
                  </ul>
                </div>

                {/* v1.4.0 */}
                <div className="border-l-4 border-indigo-500 pl-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-bold text-lg">v1.4.0</span>
                    <span className="text-xs bg-indigo-100 text-indigo-800 px-2 py-1 rounded">Improvement</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">October 5, 2025</p>
                  <h4 className="font-semibold text-indigo-700 mb-1">Unified Icon System</h4>
                  <ul className="text-sm space-y-1 text-muted-foreground list-disc list-inside">
                    <li>Migration to Iconify (72+ icons)</li>
                    <li>Sport icons on series cards</li>
                    <li>Reduced bundle size</li>
                  </ul>
                </div>

                {/* v1.3.0 */}
                <div className="border-l-4 border-orange-500 pl-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-bold text-lg">v1.3.0</span>
                    <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded">New Feature</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">October 4, 2025</p>
                  <h4 className="font-semibold text-orange-700 mb-1">Ladder Format</h4>
                  <ul className="text-sm space-y-1 text-muted-foreground list-disc list-inside">
                    <li>Classic and Aggressive ladder variants</li>
                    <li>Challenge-based competition format</li>
                    <li>Dual ranking systems (ELO + Ladder)</li>
                  </ul>
                </div>

                {/* v1.2.0 */}
                <div className="border-l-4 border-pink-500 pl-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-bold text-lg">v1.2.0</span>
                    <span className="text-xs bg-pink-100 text-pink-800 px-2 py-1 rounded">New Feature</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">October 4, 2025</p>
                  <h4 className="font-semibold text-pink-700 mb-1">Multi-Sport Expansion</h4>
                  <ul className="text-sm space-y-1 text-muted-foreground list-disc list-inside">
                    <li>Badminton, Squash, Padel, Pickleball</li>
                    <li>Extended sets range: 3, 5, or 7</li>
                  </ul>
                </div>

                {/* v1.1.0 */}
                <div className="border-l-4 border-teal-500 pl-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-bold text-lg">v1.1.0</span>
                    <span className="text-xs bg-teal-100 text-teal-800 px-2 py-1 rounded">New Feature</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">October 3, 2025</p>
                  <h4 className="font-semibold text-teal-700 mb-1">Tennis Support</h4>
                  <ul className="text-sm space-y-1 text-muted-foreground list-disc list-inside">
                    <li>Tennis match reporting with ELO</li>
                    <li>Sport icons (CircleDot for table tennis, Circle for tennis)</li>
                  </ul>
                </div>

                {/* v1.0.x patches */}
                <div className="border-l-4 border-muted-foreground pl-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-bold text-lg">v1.0.1 - v1.0.3</span>
                    <span className="text-xs bg-muted text-foreground px-2 py-1 rounded">Bug Fixes</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">October 3, 2025</p>
                  <ul className="text-sm space-y-1 text-muted-foreground list-disc list-inside">
                    <li>v1.0.3: Fixed player search filter combination</li>
                    <li>v1.0.2: Normalize gRPC configuration</li>
                    <li>v1.0.1: Removed unused code</li>
                  </ul>
                </div>

                {/* v1.0.0 */}
                <div className="border-l-4 border-foreground pl-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-bold text-lg">v1.0.0</span>
                    <span className="text-xs bg-gray-700 text-white px-2 py-1 rounded">Initial Release</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">October 2, 2025</p>
                  <h4 className="font-semibold text-muted-foreground mb-1">First Release</h4>
                  <ul className="text-sm space-y-1 text-muted-foreground list-disc list-inside">
                    <li>Complete table tennis tournament management</li>
                    <li>Go backend with gRPC/REST + MongoDB</li>
                    <li>React TypeScript frontend</li>
                    <li>ELO rating system and leaderboard</li>
                    <li>Magic link authentication</li>
                    <li>Swedish and English translations</li>
                  </ul>
                </div>

              </div>
              
              <div className="mt-4 p-3 bg-muted rounded text-sm text-muted-foreground">
                📖 Full changelog available in the project's CHANGELOG.md
              </div>
            </CollapsibleContent>
          </Collapsible>
        </CardContent>
      </Card>

      {/* Support */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Icon icon={ICONS.info} className="text-info" />
            Help and Support
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-muted-foreground">
            If you find bugs or have feature suggestions, contact your
            club administrator or email <a href="mailto:goran@goencoder.se" className="text-info hover:underline">goran@goencoder.se</a>.
          </p>
          <div className="bg-info-muted border border-info/30 rounded p-4 text-info-muted-foreground">
            <p className="text-sm">
              <strong>Version:</strong> {APP_VERSION}
            </p>
            <p className="text-sm mt-1">
              <strong>Support:</strong> <a href="mailto:goran@goencoder.se" className="text-info hover:underline">goran@goencoder.se</a>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
